import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AdminFormPage,
  saveErrorToast,
  useAdminFormPersistence,
  type PageTab,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import useAccountHasEntitlement from "@common/hooks/useAccountHasEntitlement";
import { useV2GetBusinessUnitQuery } from "@generated";
import { BusinessUnitForm } from "../BusinessUnitForm/BusinessUnitForm";
import { TermsConditions } from "../TermsAndConditions/TermsConditions";
import { useBusinessUnitMutations } from "../api";
import { emptyBusinessUnitForm } from "../types";
import type { BusinessUnitEditTab, BusinessUnitFormValues } from "../types";

const DETAILS_FORM_ID = "business-unit-form";
const TERMS_FORM_ID = "terms-conditions-form";

export const Edit = (): JSX.Element => {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { hasEntitlement } = useAccountHasEntitlement();
  const { update } = useBusinessUnitMutations();
  // refetchOnMountOrArgChange so re-entering Edit (or arriving from a list
  // changed elsewhere) re-requests the record rather than trusting the cache.
  const { data, isLoading, refetch } = useV2GetBusinessUnitQuery(
    { id: id },
    { refetchOnMountOrArgChange: true }
  );
  const { toast, setToast, savedInitial, formKey, markSaved } =
    useAdminFormPersistence<BusinessUnitFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] =
    useState<BusinessUnitEditTab>("business_unit");

  const tabs: PageTab[] = [
    { key: "business_unit", label: "Business Unit" },
    ...(hasEntitlement("terms-and-conditions")
      ? [{ key: "special_conditions", label: "Terms & Conditions" }]
      : []),
  ];

  const loadedInitial = useMemo<BusinessUnitFormValues>(() => {
    const attrs = data?.data?.attributes;
    if (!attrs) return emptyBusinessUnitForm;
    return {
      name: attrs.name ?? "",
      externalName: attrs["external-name"] ?? "",
      streetAddress: attrs["street-address"] ?? "",
      street2: attrs.street2 ?? "",
      city: attrs.city ?? "",
      state: attrs.state ?? "",
      postalCode: attrs["postal-code"] ?? "",
      country: attrs.country ?? "",
    };
  }, [data]);

  if (isLoading) return <ScopeStackSpinner />;

  const initialValues = savedInitial ?? loadedInitial;

  const handleSubmit = async (values: BusinessUnitFormValues) => {
    setSubmitting(true);
    try {
      await update(id, values);
      markSaved(values, `'${values.name}' saved`);
      // The generated API defines no cache tags, so the update can't
      // invalidate the cached GET. Refetch so the cache holds the
      // server-fresh record for the next time Edit is opened.
      refetch();
    } catch (err) {
      console.error("Failed to update business unit", err);
      // Same reason-bearing toast as New; the API's 422 shape is identical on
      // create and update.
      setToast(saveErrorToast(err, "business unit"));
    } finally {
      setSubmitting(false);
    }
  };

  const formId =
    activeTab === "business_unit"
      ? DETAILS_FORM_ID
      : activeTab === "special_conditions"
      ? TERMS_FORM_ID
      : undefined;

  return (
    <>
      <AdminFormPage
        title="Edit Business Unit"
        formId={formId}
        onCancel={() => navigate("../..", { relative: "path" })}
        isSubmitting={submitting}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(key) => setActiveTab(key as BusinessUnitEditTab)}
      >
        {activeTab === "business_unit" && (
          <BusinessUnitForm
            key={formKey}
            id={DETAILS_FORM_ID}
            initialValues={initialValues}
            isSubmitting={submitting}
            onSubmit={handleSubmit}
          />
        )}
        {activeTab === "special_conditions" && (
          <TermsConditions
            businessUnitId={id}
            formId={TERMS_FORM_ID}
            onSubmittingChange={setSubmitting}
          />
        )}
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default Edit;
